import React, { useEffect, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Map as MapIcon, Loader2, Crosshair, Users } from 'lucide-react';
import { serverStore } from '../../stores/server.store';
import { apiFetch } from '../../utils/api';
import { cn } from '@/utils/cn';
import { Select } from '../design/Select';

/**
 * Live-Weltkarte: echte Region-Tiles vom Backend (aus den .mca-Dateien
 * gerendert) auf einem pan-/zoombaren Canvas, mit Spieler-Markern,
 * Rand-Bubbles fuer Off-Screen-Spieler und Dimensions-Wechsel.
 */

interface MapPlayer { name: string; x: number; y: number; z: number; dimension: string; health: number | null }
interface MapInfo {
  dimensions: Record<string, { regions: { rx: number; rz: number }[]; dimensionKey: string; label?: string }>;
  spawn: { x: number; z: number };
}

type TileState = { status: 'loading' | 'ok' | 'missing'; bmp?: ImageBitmap };

const VANILLA_LABELS: Record<string, string> = { overworld: 'Oberwelt', nether: 'Nether', end: 'End' };

const REGION = 512;
const MIN_SCALE = 0.12;
const MAX_SCALE = 8;

// Tile-Cache auf MODUL-Ebene: ueberlebt Tab-Wechsel/Unmounts, damit die
// Karte beim Zurueckkommen sofort steht statt alles neu zu laden (das
// verursachte frueher Request-Fluten -> Rate-Limit -> "ausgeloggt").
const tileCache = new Map<string, TileState>();
const TILE_CACHE_MAX = 400;
const rememberTile = (key: string, state: TileState) => {
  if (tileCache.size >= TILE_CACHE_MAX) {
    // aeltesten Eintrag verdraengen (Map haelt Einfuege-Reihenfolge)
    const oldest = tileCache.keys().next().value;
    if (oldest !== undefined) tileCache.delete(oldest);
  }
  tileCache.set(key, state);
};

// Max. 6 Tile-Downloads gleichzeitig - schont Server (Render-Queue) und Netz.
let tileInFlight = 0;
const tileQueue: Array<() => void> = [];
const acquireTileSlot = () => new Promise<void>((resolve) => {
  if (tileInFlight < 6) { tileInFlight++; resolve(); }
  else tileQueue.push(() => { tileInFlight++; resolve(); });
});
const releaseTileSlot = () => {
  tileInFlight--;
  const next = tileQueue.shift();
  if (next) next();
};

export const MapTab = observer(() => {
  const { activeServer } = serverStore;
  const serverId = activeServer?.id || '';
  const online = activeServer?.status === 'online';

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [info, setInfo] = useState<MapInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [dim, setDim] = useState('overworld');
  const [players, setPlayers] = useState<MapPlayer[]>([]);
  const [follow, setFollow] = useState<string | null>(null);
  const [cursor, setCursor] = useState<{ x: number; z: number } | null>(null);
  const [loadingTiles, setLoadingTiles] = useState(0);

  // Viewport als Ref: Pan/Zoom ohne React-Re-Render (fluessig).
  const view = useRef({ cx: 0, cz: 0, scale: 0.75 });
  const headImgs = useRef(new Map<string, HTMLImageElement>());
  const regionSet = useRef(new Set<string>());
  const drawQueued = useRef(false);
  const playersRef = useRef<MapPlayer[]>([]);
  const dimRef = useRef(dim);
  const followRef = useRef(follow);
  const infoRef = useRef<MapInfo | null>(null);
  dimRef.current = dim;
  followRef.current = follow;
  playersRef.current = players;
  infoRef.current = info;

  // Slug <-> Minecraft-Dimension-Key Mapping, dynamisch aus map/info
  // (inkl. modded Dimensionen wie jamd:mining oder twilightforest).
  const slugByKey = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const [slug, d] of Object.entries(info?.dimensions || {})) m[d.dimensionKey] = slug;
    return m;
  }, [info]);
  const dimLabel = (slug: string) =>
    VANILLA_LABELS[slug] || info?.dimensions[slug]?.label || slug.split('__').pop()?.replace(/_/g, ' ') || slug;

  // ------------------------------------------------------ Daten laden ----

  useEffect(() => {
    let alive = true;
    setInfoLoading(true);
    apiFetch(`/api/servers/${serverId}/map/info`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d.success) return;
        setInfo(d);
        const dims = Object.keys(d.dimensions);
        if (dims.length > 0 && !d.dimensions[dimRef.current]) setDim(dims[0]);
        view.current.cx = d.spawn?.x || 0;
        view.current.cz = d.spawn?.z || 0;
      })
      .catch(() => {})
      .finally(() => alive && setInfoLoading(false));
    return () => { alive = false; };
  }, [serverId]);

  // Spieler-Positionen pollen (nur wenn online).
  useEffect(() => {
    if (!online) { setPlayers([]); return; }
    let alive = true;
    const poll = async () => {
      try {
        const res = await apiFetch(`/api/servers/${serverId}/map/players`);
        const d = await res.json();
        if (!alive || !d.success) return;
        setPlayers(d.players);
        // Follow: Kamera nachziehen + ggf. Dimension wechseln
        const f = followRef.current;
        if (f) {
          const p = (d.players as MapPlayer[]).find((pl) => pl.name === f);
          if (p) {
            const dims = infoRef.current?.dimensions || {};
            const targetDim = Object.keys(dims).find((s) => dims[s].dimensionKey === p.dimension);
            if (targetDim && targetDim !== dimRef.current) setDim(targetDim);
            view.current.cx = p.x;
            view.current.cz = p.z;
            requestDraw();
          }
        }
      } catch { /* naechster Poll */ }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, online]);

  // Vorhandene Regionen der aktiven Dimension als Set (kein 404-Spam).
  useEffect(() => {
    regionSet.current = new Set(
      (info?.dimensions[dim]?.regions || []).map((r) => `${r.rx},${r.rz}`)
    );
    requestDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, dim]);

  // ------------------------------------------------------ Tile-Loader ----

  const loadTile = useCallback((d: string, rx: number, rz: number) => {
    const key = `${serverId}:${d}:${rx},${rz}`;
    if (tileCache.has(key)) return;
    rememberTile(key, { status: 'loading' });
    setLoadingTiles((n) => n + 1);
    (async () => {
      await acquireTileSlot();
      try {
        const res = await apiFetch(`/api/servers/${serverId}/map/tile/${encodeURIComponent(d)}/${rx}/${rz}`);
        if (!res.ok) throw new Error('missing');
        const blob = await res.blob();
        const bmp = await createImageBitmap(blob);
        rememberTile(key, { status: 'ok', bmp });
      } catch {
        rememberTile(key, { status: 'missing' });
      } finally {
        releaseTileSlot();
        setLoadingTiles((n) => Math.max(0, n - 1));
        requestDraw();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  // ---------------------------------------------------------- Zeichnen ----

  const draw = useCallback(() => {
    drawQueued.current = false;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;

    // Hintergrund
    ctx.fillStyle = '#0b0c10';
    ctx.fillRect(0, 0, w, h);

    const { cx, cz, scale } = view.current;
    const worldLeft = cx - w / 2 / scale;
    const worldTop = cz - h / 2 / scale;

    // Sichtbare Regionen zeichnen / laden
    const r0x = Math.floor(worldLeft / REGION);
    const r0z = Math.floor(worldTop / REGION);
    const r1x = Math.floor((cx + w / 2 / scale) / REGION);
    const r1z = Math.floor((cz + h / 2 / scale) / REGION);

    for (let rz = r0z; rz <= r1z; rz++) {
      for (let rx = r0x; rx <= r1x; rx++) {
        if (!regionSet.current.has(`${rx},${rz}`)) continue;
        const key = `${serverId}:${dimRef.current}:${rx},${rz}`;
        const tile = tileCache.get(key);
        const sx = (rx * REGION - worldLeft) * scale;
        const sz = (rz * REGION - worldTop) * scale;
        if (tile?.status === 'ok' && tile.bmp) {
          ctx.drawImage(tile.bmp, sx, sz, REGION * scale, REGION * scale);
        } else {
          if (!tile) loadTile(dimRef.current, rx, rz);
          ctx.fillStyle = 'rgba(255,255,255,0.03)';
          ctx.fillRect(sx, sz, REGION * scale, REGION * scale);
        }
      }
    }

    // Spieler derselben Dimension
    const dimKey = infoRef.current?.dimensions[dimRef.current]?.dimensionKey;
    const sameDim = playersRef.current.filter((p) => p.dimension === dimKey);
    const margin = 26;

    for (const p of sameDim) {
      const sx = (p.x - worldLeft) * scale;
      const sz = (p.z - worldTop) * scale;
      const onScreen = sx >= -16 && sx <= w + 16 && sz >= -16 && sz <= h + 16;

      let img = headImgs.current.get(p.name);
      if (!img) {
        img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = `https://mc-heads.net/avatar/${p.name}/32`;
        img.onload = () => requestDraw();
        headImgs.current.set(p.name, img);
      }

      if (onScreen) {
        // Marker: Kopf + Name-Pill
        if (img.complete && img.naturalWidth > 0) {
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(sx - 12, sz - 12, 24, 24, 5);
          ctx.strokeStyle = followRef.current === p.name ? '#ff6a3d' : '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.clip();
          ctx.drawImage(img, sx - 12, sz - 12, 24, 24);
          ctx.restore();
        } else {
          ctx.fillStyle = '#ff6a3d';
          ctx.beginPath();
          ctx.arc(sx, sz, 6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.font = 'bold 11px ui-sans-serif, system-ui';
        const tw = ctx.measureText(p.name).width;
        ctx.fillStyle = 'rgba(0,0,0,0.72)';
        ctx.beginPath();
        ctx.roundRect(sx - tw / 2 - 6, sz + 15, tw + 12, 17, 8);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.fillText(p.name, sx, sz + 27);
      } else {
        // Rand-Bubble in echter Richtung (geclampte Position)
        const bx = Math.max(margin, Math.min(w - margin, sx));
        const bz = Math.max(margin, Math.min(h - margin, sz));
        ctx.save();
        ctx.beginPath();
        ctx.arc(bx, bz, 16, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(10,10,14,0.85)';
        ctx.fill();
        ctx.strokeStyle = '#ff6a3d';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.clip();
        if (img.complete && img.naturalWidth > 0) ctx.drawImage(img, bx - 13, bz - 13, 26, 26);
        ctx.restore();
        // Richtungspfeil
        const ang = Math.atan2(sz - bz, sx - bx);
        ctx.save();
        ctx.translate(bx + Math.cos(ang) * 21, bz + Math.sin(ang) * 21);
        ctx.rotate(ang);
        ctx.beginPath();
        ctx.moveTo(4, 0); ctx.lineTo(-3, -4); ctx.lineTo(-3, 4);
        ctx.closePath();
        ctx.fillStyle = '#ff6a3d';
        ctx.fill();
        ctx.restore();
      }
    }
  }, [loadTile]);

  const requestDraw = useCallback(() => {
    if (drawQueued.current) return;
    drawQueued.current = true;
    requestAnimationFrame(draw);
  }, [draw]);

  useEffect(() => { requestDraw(); }, [players, dim, requestDraw]);

  // Resize beobachten
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => requestDraw());
    ro.observe(el);
    return () => ro.disconnect();
  }, [requestDraw]);

  // ------------------------------------------------- Maus-Interaktion ----

  const dragState = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const screenToWorld = (e: React.PointerEvent | React.WheelEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const { cx, cz, scale } = view.current;
    return {
      x: cx + (e.clientX - rect.left - rect.width / 2) / scale,
      z: cz + (e.clientY - rect.top - rect.height / 2) / scale,
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    dragState.current = { x: e.clientX, y: e.clientY, moved: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const world = screenToWorld(e);
    setCursor({ x: Math.round(world.x), z: Math.round(world.z) });
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.x;
    const dy = e.clientY - dragState.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) {
      dragState.current.moved = true;
      if (followRef.current) setFollow(null); // manuelles Pannen beendet Follow
    }
    view.current.cx -= dx / view.current.scale;
    view.current.cz -= dy / view.current.scale;
    dragState.current.x = e.clientX;
    dragState.current.y = e.clientY;
    requestDraw();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const wasClick = dragState.current && !dragState.current.moved;
    dragState.current = null;
    if (!wasClick) return;
    // Klick: Spieler in der Naehe? -> folgen
    const rect = canvasRef.current!.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const { cx, cz, scale } = view.current;
    const w = rect.width, h = rect.height;
    const dimKey = infoRef.current?.dimensions[dimRef.current]?.dimensionKey;
    for (const p of playersRef.current.filter((pl) => pl.dimension === dimKey)) {
      let sx = (p.x - (cx - w / 2 / scale)) * scale;
      let sz = (p.z - (cz - h / 2 / scale)) * scale;
      // Off-Screen-Bubble-Position beruecksichtigen
      sx = Math.max(26, Math.min(w - 26, sx));
      sz = Math.max(26, Math.min(h - 26, sz));
      if (Math.hypot(mx - sx, my - sz) < 20) {
        setFollow(p.name);
        view.current.cx = p.x;
        view.current.cz = p.z;
        requestDraw();
        return;
      }
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    const before = screenToWorld(e);
    const factor = e.deltaY < 0 ? 1.18 : 1 / 1.18;
    view.current.scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, view.current.scale * factor));
    const after = screenToWorld(e);
    view.current.cx += before.x - after.x;
    view.current.cz += before.z - after.z;
    requestDraw();
  };

  const jumpToSpawn = () => {
    setFollow(null);
    view.current.cx = info?.spawn?.x || 0;
    view.current.cz = info?.spawn?.z || 0;
    requestDraw();
  };

  // ---------------------------------------------------------- Renders ----

  const availableDims = Object.keys(info?.dimensions || {});
  const vanillaDims = availableDims.filter((d) => VANILLA_LABELS[d]);
  const moddedDims = availableDims.filter((d) => !VANILLA_LABELS[d]);
  const dimKey = info?.dimensions[dim]?.dimensionKey;
  const unmappedPlayers = players.filter((p) => !slugByKey[p.dimension]);

  if (infoLoading) {
    return (
      <div className="glass-panel rounded-xl flex items-center justify-center h-[60vh]">
        <Loader2 size={32} className="animate-spin text-ember" />
      </div>
    );
  }

  if (availableDims.length === 0) {
    return (
      <div className="glass-panel rounded-xl flex flex-col items-center justify-center h-[60vh] text-fg-muted">
        <MapIcon size={40} className="mb-3 opacity-25" />
        <p className="font-display text-lg font-semibold text-fg">Noch keine Karte</p>
        <p className="text-sm mt-1 max-w-sm text-center">Die Karte erscheint, sobald die Welt einmal erzeugt und gespeichert wurde – also nach dem ersten Start des Servers.</p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-xl overflow-hidden flex flex-col h-[calc(100vh-160px)] min-h-[480px] animate-in fade-in duration-300">
      {/* Kopfleiste */}
      <div className="bg-white/5 px-4 py-2.5 border-b border-line flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MapIcon size={17} className="text-ember" />
          <span className="font-bold text-white text-sm">Live-Karte</span>
          {loadingTiles > 0 && (
            <span className="text-[10px] text-fg-muted flex items-center gap-1">
              <Loader2 size={10} className="animate-spin" /> rendert {loadingTiles} Region{loadingTiles > 1 ? 'en' : ''}…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {vanillaDims.map((d) => (
            <button
              key={d}
              onClick={() => { setDim(d); setFollow(null); }}
              className={cn(
                'px-2.5 py-1 rounded-lg text-xs font-bold transition-all border',
                dim === d
                  ? d === 'nether' ? 'bg-red-500/15 text-red-400 border-red-500/40'
                    : d === 'end' ? 'bg-purple-500/15 text-purple-400 border-purple-500/40'
                    : 'bg-green-500/15 text-green-400 border-green-500/40'
                  : 'bg-white/5 text-fg-muted border-line hover:text-white'
              )}
            >
              {VANILLA_LABELS[d]}
            </button>
          ))}
          {/* Modded Dimensionen (Twilight Forest, Mining-Dim, ...) */}
          {moddedDims.length > 0 && (
            <Select
              className="w-44"
              buttonClassName={cn(
                'text-xs py-1 font-bold',
                moddedDims.includes(dim) && 'text-blue-400 border-blue-500/40'
              )}
              placeholder="Mod-Dimensionen…"
              value={moddedDims.includes(dim) ? dim : ''}
              onChange={(v) => { if (v) { setDim(v); setFollow(null); } }}
              options={moddedDims.map((d) => ({ value: d, label: dimLabel(d) }))}
            />
          )}
          <div className="w-px h-5 bg-line mx-1" />
          <button
            onClick={jumpToSpawn}
            title="Zum Spawn"
            className="p-1.5 rounded-lg bg-white/5 border border-line text-fg-muted hover:text-white transition-colors"
          >
            <Crosshair size={14} />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="relative flex-1 min-h-0">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none cursor-grab active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => { dragState.current = null; setCursor(null); }}
          onWheel={onWheel}
        />
        {/* Koordinaten-HUD */}
        {cursor && (
          <div className="absolute bottom-3 left-3 bg-black/70 border border-line rounded-lg px-2.5 py-1 text-[11px] font-mono text-fg pointer-events-none">
            X {cursor.x} / Z {cursor.z}
          </div>
        )}
        {follow && (
          <div className="absolute top-3 left-3 bg-ember/15 border border-ember/40 rounded-lg px-2.5 py-1 text-[11px] font-bold text-ember flex items-center gap-1.5">
            <Crosshair size={11} /> Folgt {follow} — zum Beenden Karte bewegen
          </div>
        )}
        {!online && (
          <div className="absolute top-3 right-3 bg-black/70 border border-line rounded-lg px-2.5 py-1 text-[11px] text-fg-muted pointer-events-none">
            Server offline — Karte ohne Live-Spieler
          </div>
        )}
      </div>

      {/* Spieler-Leiste */}
      {players.length > 0 && (
        <div className="border-t border-line bg-black/30 px-3 py-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold text-fg-muted flex items-center gap-1">
            <Users size={11} /> {players.length} online
          </span>
          {players.map((p) => {
            const pDim = slugByKey[p.dimension];
            const isHere = p.dimension === dimKey;
            return (
              <button
                key={p.name}
                onClick={() => {
                  if (pDim) setDim(pDim);
                  setFollow(p.name);
                  view.current.cx = p.x;
                  view.current.cz = p.z;
                  requestDraw();
                }}
                title={pDim ? `Zu ${p.name} springen` : `${p.name} ist in ${p.dimension} — Welt noch nicht generiert/gespeichert`}
                disabled={!pDim}
                className={cn(
                  'flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-[11px] font-bold transition-all',
                  follow === p.name
                    ? 'bg-ember/15 border-ember/50 text-ember'
                    : isHere
                      ? 'bg-white/5 border-line text-white hover:border-line-strong'
                      : pDim
                        ? 'bg-white/5 border-line text-fg-muted hover:text-white'
                        : 'bg-white/5 border-line text-fg-muted opacity-60 cursor-not-allowed'
                )}
              >
                <img
                  src={`https://mc-heads.net/avatar/${p.name}/16`}
                  className="w-4 h-4 rounded"
                  style={{ imageRendering: 'pixelated' }}
                  alt=""
                />
                {p.name}
                {!isHere && pDim && <span className="text-[9px] opacity-70">({dimLabel(pDim)})</span>}
                {!pDim && <span className="text-[9px] opacity-70">(keine Karte)</span>}
              </button>
            );
          })}
          {unmappedPlayers.length > 0 && (
            <span className="text-[10px] text-fg-muted italic">Dimension ohne gespeicherte Regionen = noch keine Karte</span>
          )}
        </div>
      )}
    </div>
  );
});
